import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import styles from './TextInput.module.css';

export interface TextInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** email/password/text. For `password`, a show/hide toggle is rendered. */
  type?: 'email' | 'password' | 'text';
}

/**
 * Text input with an accessible password show/hide toggle. The toggle is a real
 * <button> with `aria-pressed` and an `aria-label` that describes its action;
 * pressing it swaps the input `type` between password and text.
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { type = 'text', className, ...rest },
  ref,
) {
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  const effectiveType = isPassword && revealed ? 'text' : type;

  const inputClasses = [styles.input, isPassword ? styles.hasToggle : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <span className={styles.wrapper}>
      <input ref={ref} type={effectiveType} className={inputClasses} {...rest} />
      {isPassword ? (
        <button
          type="button"
          className={styles.toggle}
          aria-pressed={revealed}
          aria-label={revealed ? 'Hide password' : 'Show password'}
          onClick={() => setRevealed((v) => !v)}
        >
          {revealed ? 'Hide' : 'Show'}
        </button>
      ) : null}
    </span>
  );
});
